import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Loader2, Search, Filter, Percent, Building2, CheckCircle, XCircle, Mail, Send, Clock, DollarSign, TrendingUp, Trophy, CreditCard, Eye, X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Comisionistas() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comisionistas, setComisionistas] = useState([]);
  const [invitaciones, setInvitaciones] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [resumen, setResumen] = useState(null);

  const [inviteDialog, setInviteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, userId: null, nombre: '' });
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagoDialog, setPagoDialog] = useState({ open: false, comision: null });
  const [pagoForm, setPagoForm] = useState({ monto: '', metodo: 'transferencia', referencia: '' });

  const [filterFracc, setFilterFracc] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [inviteForm, setInviteForm] = useState({ email: '', fraccionamiento_id: '', porcentaje_base: '5' });
  const [editForm, setEditForm] = useState({ porcentaje_comision: '', activo: true });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!res.ok) { navigate('/login'); return; }
        setUser(await res.json());
      } catch { navigate('/login'); }
    };
    checkAuth();
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterFracc !== 'todos' ? `?fraccionamiento_id=${filterFracc}` : '';
      const [comRes, fraccsRes, invRes, resRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/comisionistas${params}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/comisionistas/invitaciones-pendientes`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/comisiones/resumen`, { credentials: 'include' })
      ]);
      if (comRes.ok) setComisionistas(await comRes.json());
      if (fraccsRes.ok) setFraccionamientos(await fraccsRes.json());
      if (invRes.ok) setInvitaciones(await invRes.json());
      if (resRes.ok) setResumen(await resRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterFracc]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const filtered = useMemo(() => {
    if (!searchTerm) return comisionistas;
    const term = searchTerm.toLowerCase();
    return comisionistas.filter(c =>
      c.user_nombre?.toLowerCase().includes(term) ||
      c.user_email?.toLowerCase().includes(term) ||
      c.fraccionamiento_nombre?.toLowerCase().includes(term)
    );
  }, [comisionistas, searchTerm]);

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.fraccionamiento_id || !inviteForm.porcentaje_base) {
      toast.error('Todos los campos son obligatorios'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comisionistas/invitar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          email: inviteForm.email,
          fraccionamiento_id: inviteForm.fraccionamiento_id,
          porcentaje_base: parseFloat(inviteForm.porcentaje_base),
          origin_url: window.location.origin
        })
      });
      const clone = res.clone();
      let data = {};
      try { data = await clone.json(); } catch { try { data = { raw: await res.text() }; } catch { data = {}; } }
      if (res.ok) {
        toast.success(data.email_sent ? 'Invitación enviada por correo' : 'Invitación creada (email no enviado)');
        setInviteDialog(false);
        setInviteForm({ email: '', fraccionamiento_id: '', porcentaje_base: '5' });
        await loadData();
      } else {
        const errorMsg = data.detail || data.raw || `Error ${res.status}`;
        toast.error(errorMsg);
      }
    } catch (err) { toast.error(`Error de conexión: ${err.message}`); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!editDialog.item) return;
    setSubmitting(true);
    try {
      const body = {};
      if (editForm.porcentaje_comision) body.porcentaje_comision = parseFloat(editForm.porcentaje_comision);
      body.activo = editForm.activo;
      // Update all registros for this user
      const registros = editDialog.item.registros || [editDialog.item];
      for (const r of registros) {
        await fetch(`${BACKEND_URL}/api/comisionistas/${r.comisionista_id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(body)
        });
      }
      toast.success('Comisionista actualizado');
      setEditDialog({ open: false, item: null });
      await loadData();
    } catch { toast.error('Error de conexión'); }
    finally { setSubmitting(false); }
  };

  const loadDetail = async (userId, nombre) => {
    setDetailDialog({ open: true, userId, nombre });
    setDetailLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comisionistas/${userId}/comisiones`, { credentials: 'include' });
      if (res.ok) setDetailData(await res.json());
      else toast.error('Error al cargar comisiones');
    } catch { toast.error('Error de conexión'); }
    finally { setDetailLoading(false); }
  };

  const handlePago = async () => {
    if (!pagoDialog.comision || !pagoForm.monto) { toast.error('Ingresa un monto'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comisiones/${pagoDialog.comision.comision_id}/pagar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          monto: parseFloat(pagoForm.monto),
          metodo: pagoForm.metodo,
          referencia: pagoForm.referencia || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Pago registrado');
        setPagoDialog({ open: false, comision: null });
        setPagoForm({ monto: '', metodo: 'transferencia', referencia: '' });
        if (detailDialog.userId) await loadDetail(detailDialog.userId, detailDialog.nombre);
        await loadData();
      } else {
        toast.error(data.detail || 'Error al registrar pago');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setSubmitting(false); }
  };

  const getTimeLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Expirada';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  if (!user) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>;
  }

  return (
    <div data-testid="comisionistas-page" className="p-4 sm:p-6 space-y-5 max-w-[1200px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-6 h-6 text-[#C6A54A]" />
            Comisionistas
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Gestiona tu red comercial y porcentajes de comisión</p>
        </div>
        <Button data-testid="invite-comisionista-btn" onClick={() => setInviteDialog(true)} className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-semibold h-10">
          <Send className="w-4 h-4 mr-2" /> Invitar Comisionista
        </Button>
      </div>

      {/* Mini Dashboard de Comisiones */}
      {resumen && (
        <div data-testid="comisiones-dashboard" className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#C6A54A]" />
                <span className="text-xs text-zinc-500 uppercase tracking-wider">Generadas</span>
              </div>
              <p data-testid="resumen-generado" className="text-xl font-bold text-[#C6A54A]">${fmt(resumen.total_generado)}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-zinc-500 uppercase tracking-wider">Pagadas</span>
              </div>
              <p data-testid="resumen-pagado" className="text-xl font-bold text-emerald-400">${fmt(resumen.total_pagado)}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-zinc-500 uppercase tracking-wider">Pendientes</span>
              </div>
              <p data-testid="resumen-pendiente" className="text-xl font-bold text-amber-400">${fmt(resumen.total_pendiente)}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-zinc-500 uppercase tracking-wider">% s/Ventas</span>
              </div>
              <p data-testid="resumen-porcentaje" className="text-xl font-bold text-blue-400">{resumen.porcentaje_sobre_ventas}%</p>
              <p className="text-xs text-zinc-600 mt-0.5">{resumen.total_ventas} ventas · ${fmt(resumen.total_ventas_monto)}</p>
            </div>
          </div>

          {/* Top 5 */}
          {resumen.top_comisionistas?.length > 0 && (
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
              <h3 className="text-sm font-semibold text-zinc-400 flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#C6A54A]" /> Top Comisionistas por Ventas
              </h3>
              <div className="space-y-2">
                {resumen.top_comisionistas.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-zinc-800/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#C6A54A] text-black' : i === 1 ? 'bg-zinc-600 text-white' : 'bg-zinc-700 text-zinc-400'}`}>{i + 1}</span>
                      <span className="text-white text-sm font-medium">{t.nombre}</span>
                      <span className="text-xs text-zinc-500">{t.ventas} venta{t.ventas !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-white">${fmt(t.vendido)}</span>
                      <span className="text-xs text-[#C6A54A] ml-2">(${fmt(t.comision)})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Invitations */}
      {invitaciones.length > 0 && (
        <div data-testid="invitaciones-pendientes-section">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" /> Invitaciones Pendientes
          </h2>
          <div className="space-y-2 mb-4">
            {invitaciones.map(inv => (
              <div key={inv.invitacion_id} data-testid={`invitacion-${inv.invitacion_id}`}
                className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex gap-3 items-center flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{inv.email}</p>
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{inv.fraccionamiento_nombre}</span>
                      <span className="text-[#C6A54A] font-medium">{inv.porcentaje_base}%</span>
                    </div>
                  </div>
                </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400">Pendiente</span>
                  <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1 justify-end">
                    <Clock className="w-3 h-3" /> {getTimeLeft(inv.expires_at)}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar invitación para ${inv.email}?`)) return;
                    try {
                      const res = await fetch(`${BACKEND_URL}/api/comisionistas/invitaciones/${inv.invitacion_id}`, {
                        method: 'DELETE', credentials: 'include'
                      });
                      if (res.ok) { toast.success('Invitación eliminada'); loadData(); }
                      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Error al eliminar'); }
                    } catch { toast.error('Error de conexión'); }
                  }}
                  className="w-8 h-8 rounded-full bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-colors"
                  data-testid={`delete-invitacion-${inv.invitacion_id}`}
                  title="Eliminar invitación"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comisionistas Registrados */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-400" /> Comisionistas Registrados
        </h2>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 bg-zinc-900/50 rounded-xl p-3 border border-zinc-800/50 mb-3">
          <Filter className="w-4 h-4 text-zinc-500 hidden sm:block" />
          <Select value={filterFracc} onValueChange={setFilterFracc}>
            <SelectTrigger data-testid="filter-fraccionamiento" className="w-[200px] bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
              <SelectValue placeholder="Fraccionamiento" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="todos" className="text-white">Todos</SelectItem>
              {fraccionamientos.map(f => (
                <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white">{f.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input data-testid="search-comisionistas" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar..." className="pl-9 bg-zinc-800 border-zinc-700 text-white h-9 text-sm" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
            <Users className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Sin comisionistas</h3>
            <p className="text-sm text-zinc-500 mb-6">Invita a tu primer comisionista por correo electrónico</p>
            <Button onClick={() => setInviteDialog(true)} className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black">
              <Send className="w-4 h-4 mr-2" /> Invitar Comisionista
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(c => (
              <div key={c.user_id} data-testid={`comisionista-${c.comisionista_id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex gap-3 items-start flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mt-0.5 ${c.activo ? 'bg-[#C6A54A]/20 text-[#C6A54A]' : 'bg-zinc-700 text-zinc-500'}`}>
                      {(c.user_nombre || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white text-sm">{c.user_nombre}</h3>
                        {c.activo
                          ? <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-400">Activo</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-600/15 text-zinc-500">Inactivo</span>
                        }
                        {c.fraccionamientos > 1 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/15 text-blue-400">{c.fraccionamientos} fraccs</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-zinc-400">
                        <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.user_email}</span>
                        <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{c.fraccionamiento_nombre}</span>
                      </div>
                      {/* Financial summary row */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                        <span className="text-zinc-500">{c.ventas_totales} venta{c.ventas_totales !== 1 ? 's' : ''}</span>
                        <span className="text-[#C6A54A]">Generada: ${fmt(c.comision_total_generada)}</span>
                        <span className="text-emerald-400">Pagada: ${fmt(c.comision_total_pagada)}</span>
                        {c.comision_total_pendiente > 0 && (
                          <span className="text-amber-400 font-medium">Pendiente: ${fmt(c.comision_total_pendiente)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right mr-1">
                      <span className="text-xl font-bold text-[#C6A54A]">{c.porcentaje_comision}%</span>
                    </div>
                    <Button data-testid={`view-comisiones-${c.user_id}`} variant="ghost" size="sm"
                      onClick={() => loadDetail(c.user_id, c.user_nombre)}
                      className="text-zinc-400 hover:text-white h-8 w-8 p-0" title="Ver comisiones">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`edit-comisionista-${c.comisionista_id}`} variant="ghost" size="sm"
                      onClick={() => { setEditDialog({ open: true, item: c }); setEditForm({ porcentaje_comision: c.porcentaje_comision.toString(), activo: c.activo }); }}
                      className="text-zinc-400 hover:text-white h-8 w-8 p-0" title="Editar">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal: Comisiones por comisionista */}
      <Dialog open={detailDialog.open} onOpenChange={open => !open && setDetailDialog({ open: false, userId: null, nombre: '' })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C6A54A]" /> Comisiones de {detailDialog.nombre}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">Detalle de cada comisión y pagos registrados</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#C6A54A] animate-spin" /></div>
          ) : detailData.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">Sin comisiones registradas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {detailData.map(c => (
                <div key={c.comision_id} data-testid={`comision-detail-${c.comision_id}`}
                  className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-white font-semibold">Lote #{c.lote_numero} <span className="text-zinc-500 text-xs">Mz {c.lote_manzana}</span></p>
                      <p className="text-xs text-zinc-400 mt-0.5">{c.fraccionamiento_nombre} · Cliente: {c.cliente_nombre || '-'}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.estado === 'pagada' ? 'bg-emerald-500/20 text-emerald-400' :
                      c.estado === 'parcial' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-zinc-700 text-zinc-300'
                    }`}>{c.estado === 'pagada' ? 'Pagada' : c.estado === 'parcial' ? 'Parcial' : 'Pendiente'}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                    <div>
                      <p className="text-zinc-500">Precio Venta</p>
                      <p className="text-white font-medium">${fmt(c.precio_venta)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">% Aplicado</p>
                      <p className="text-[#C6A54A] font-medium">{c.porcentaje_aplicado}%</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Comisión</p>
                      <p className="text-white font-semibold">${fmt(c.monto_total)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Pendiente</p>
                      <p className={`font-semibold ${c.saldo_pendiente > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        ${fmt(c.saldo_pendiente)}
                      </p>
                    </div>
                  </div>
                  {/* Liquidation type */}
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <span className="text-zinc-500">Liquidación:</span>
                    <span className="px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">
                      {c.tipo_liquidacion === 'enganche' ? 'Pagada con enganche' : c.tipo_liquidacion === 'pagos' ? `En ${c.numero_pagos || '?'} pagos` : 'Pago único'}
                    </span>
                  </div>
                  {/* Scheduled payments */}
                  {c.pagos_programados?.length > 0 && (
                    <div className="border-t border-zinc-700 pt-2 mb-2">
                      <p className="text-xs text-zinc-500 mb-1">Plan de pagos:</p>
                      {c.pagos_programados.map(pp => (
                        <div key={pp.pago_programado_id} className="flex items-center justify-between text-xs py-1">
                          <span className="text-zinc-400">Pago {pp.numero_pago} · {new Date(pp.fecha_programada).toLocaleDateString('es-MX')}</span>
                          <div className="flex items-center gap-2">
                            <span className={pp.estado === 'pagado' ? 'text-emerald-400' : 'text-white'}>${fmt(pp.monto)}</span>
                            <span className={`text-[10px] px-1 rounded ${pp.estado === 'pagado' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-600 text-zinc-400'}`}>{pp.estado === 'pagado' ? 'OK' : 'Pend'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Pagos list */}
                  {c.pagos?.length > 0 && (
                    <div className="border-t border-zinc-700 pt-2 mb-2">
                      <p className="text-xs text-zinc-500 mb-1">Pagos realizados:</p>
                      {c.pagos.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1">
                          <span className="text-zinc-400">{new Date(p.fecha).toLocaleDateString('es-MX')} · {p.metodo}{p.referencia ? ` · ${p.referencia}` : ''}</span>
                          <span className="text-emerald-400 font-medium">${fmt(p.monto)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {c.saldo_pendiente > 0 && (
                    <Button data-testid={`pagar-comision-${c.comision_id}`} size="sm"
                      onClick={() => { setPagoDialog({ open: true, comision: c }); setPagoForm({ monto: c.saldo_pendiente.toString(), metodo: 'transferencia', referencia: '' }); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 mt-1">
                      <CreditCard className="w-3 h-3 mr-1" /> Registrar Pago
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pago Dialog */}
      <Dialog open={pagoDialog.open} onOpenChange={open => !open && setPagoDialog({ open: false, comision: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-400" /> Registrar Pago</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {pagoDialog.comision && `Lote #${pagoDialog.comision.lote_numero} · Saldo: $${fmt(pagoDialog.comision.saldo_pendiente)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Monto *</Label>
              <Input data-testid="pago-monto" type="number" step="0.01" min="0"
                value={pagoForm.monto} onChange={e => setPagoForm(p => ({...p, monto: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Método</Label>
              <Select value={pagoForm.metodo} onValueChange={v => setPagoForm(p => ({...p, metodo: v}))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="transferencia" className="text-white">Transferencia</SelectItem>
                  <SelectItem value="efectivo" className="text-white">Efectivo</SelectItem>
                  <SelectItem value="cheque" className="text-white">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Referencia (opcional)</Label>
              <Input data-testid="pago-referencia" value={pagoForm.referencia}
                onChange={e => setPagoForm(p => ({...p, referencia: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" placeholder="No. de operación" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setPagoDialog({ open: false, comision: null })} className="text-zinc-400">Cancelar</Button>
              <Button data-testid="confirm-pago-btn" onClick={handlePago} disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />} Pagar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-[#C6A54A]" /> Invitar Comisionista</DialogTitle>
            <DialogDescription className="text-zinc-400">Se enviará un correo con link de activación (expira en 24h)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Email *</Label>
              <Input data-testid="invite-email" type="email" value={inviteForm.email}
                onChange={e => setInviteForm(p => ({...p, email: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" placeholder="comisionista@email.com" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Fraccionamiento *</Label>
              <Select value={inviteForm.fraccionamiento_id} onValueChange={v => setInviteForm(p => ({...p, fraccionamiento_id: v}))}>
                <SelectTrigger data-testid="invite-fraccionamiento" className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {fraccionamientos.map(f => (
                    <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white">{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Porcentaje de Comisión Base *</Label>
              <div className="relative">
                <Input data-testid="invite-porcentaje" type="number" step="0.5" min="0" max="100"
                  value={inviteForm.porcentaje_base} onChange={e => setInviteForm(p => ({...p, porcentaje_base: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-white pr-10" />
                <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setInviteDialog(false)} className="text-zinc-400">Cancelar</Button>
              <Button data-testid="send-invite-btn" onClick={handleInvite} disabled={submitting}
                className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-semibold">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Enviar Invitación
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={open => !open && setEditDialog({ open: false, item: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-[#C6A54A]" /> Editar Comisionista</DialogTitle>
          </DialogHeader>
          {editDialog.item && (
            <div className="space-y-4">
              <div className="bg-zinc-800/70 rounded-lg p-3">
                <p className="font-medium text-white">{editDialog.item.user_nombre}</p>
                <p className="text-xs text-zinc-500">{editDialog.item.fraccionamiento_nombre}</p>
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Porcentaje de Comisión</Label>
                <div className="relative">
                  <Input data-testid="edit-porcentaje" type="number" step="0.5" min="0" max="100"
                    value={editForm.porcentaje_comision} onChange={e => setEditForm(p => ({...p, porcentaje_comision: e.target.value}))}
                    className="bg-zinc-800 border-zinc-700 text-white pr-10" />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-zinc-300 text-sm">Estado:</Label>
                <Button variant={editForm.activo ? 'default' : 'outline'} size="sm"
                  onClick={() => setEditForm(p => ({...p, activo: true}))}
                  className={editForm.activo ? 'bg-emerald-600 text-white' : 'border-zinc-700 text-zinc-400'}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Activo
                </Button>
                <Button variant={!editForm.activo ? 'default' : 'outline'} size="sm"
                  onClick={() => setEditForm(p => ({...p, activo: false}))}
                  className={!editForm.activo ? 'bg-red-600 text-white' : 'border-zinc-700 text-zinc-400'}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Inactivo
                </Button>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => setEditDialog({ open: false, item: null })} className="text-zinc-400">Cancelar</Button>
                <Button data-testid="save-comisionista-btn" onClick={handleUpdate} disabled={submitting}
                  className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-semibold">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Guardar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
